import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Asterisk, Copy, Unplug, Check } from "lucide-react";
import { ago, when, ClaudeBadge } from "@/components/claude/ClaudeBadge";

const CONNECTOR_URL = `${API_BASE}/mcp`;

function CopyField({ value, testid }) {
  const [done, setDone] = useState(false);
  const copy = () => navigator.clipboard?.writeText(value).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); toast.success("Copied"); })
    .catch(() => toast.error("Couldn't copy. Select the text and copy it."));
  return (
    <div className="flex items-stretch gap-2">
      <code data-testid={testid} className="flex-1 min-w-0 truncate text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono text-slate-800">{value}</code>
      <Button size="sm" variant="outline" data-testid={`${testid}-copy`} onClick={copy} className="h-auto">
        {done ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />} Copy
      </Button>
    </div>
  );
}

function SetupSteps() {
  return (
    <div className="grid sm:grid-cols-2 gap-3 text-xs text-slate-600" data-testid="claude-setup-steps">
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="font-semibold text-slate-800 mb-1">Claude.ai or Claude Desktop</div>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>Open Settings → Connectors → Add custom connector.</li>
          <li>Paste the address above and click Connect.</li>
          <li>Sign in to GridForge and click Allow.</li>
        </ol>
        <div className="mt-1.5 text-slate-400">On Team or Enterprise plans, an organization Owner may need to add the connector first.</div>
      </div>
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="font-semibold text-slate-800 mb-1">Claude Code</div>
        <code className="block bg-slate-50 rounded px-2 py-1.5 font-mono text-[11px] break-all text-slate-700">claude mcp add --transport http gridforge {CONNECTOR_URL}</code>
        <div className="mt-1.5 text-slate-400">The GridForge sign-in opens the first time Claude uses it.</div>
      </div>
    </div>
  );
}

function Connections({ s, onDisconnect }) {
  if (!s.connections.length) return <div className="text-sm text-slate-400" data-testid="claude-connections-empty">No one is connected yet.</div>;
  return (
    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="claude-connections">
      {s.connections.map((c) => (
        <div key={c.connection_id} data-testid={`claude-conn-${c.connection_id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-800"><b>{c.user_name}</b>{c.mine ? " (you)" : ""} · {c.app}</div>
            <div className="text-[11px] text-slate-400">Connected {when(c.created_at)} · {c.last_used_at ? `last used ${ago(c.last_used_at)}` : "not used yet"} · {c.tool_calls || 0} actions</div>
          </div>
          <Button size="sm" variant="outline" data-testid={`claude-disconnect-${c.connection_id}`} onClick={() => onDisconnect(c)}
            className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50"><Unplug className="w-3.5 h-3.5 mr-1" /> Disconnect</Button>
        </div>
      ))}
    </div>
  );
}

function RecentActivity({ items }) {
  if (!items.length) return <div className="text-sm text-slate-400">Nothing yet.</div>;
  return (
    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-72 overflow-auto" data-testid="claude-activity-list">
      {items.map((a) => (
        <div key={a.activity_id} className="px-3 py-2 text-sm" data-testid={`claude-activity-${a.activity_id}`}>
          <div className="text-slate-800"><span className="font-semibold">{a.user_name}</span> <ClaudeBadge /> <span className="text-slate-600">· {a.message}</span></div>
          <div className="text-[11px] text-slate-400">
            {when(a.at)}{a.bid_id && <> · <Link to={`/bids/${a.bid_id}`} className="text-[#003E87] hover:underline">{a.bid_name || "Open bid"}</Link></>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ClaudeConnectorCard() {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/claude/settings").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!s) return null;
  const toggle = async (v) => {
    try { await api.put("/claude/settings", { enabled: v }); toast.success(v ? "Claude connector is on" : "Claude connector is off"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const toggleAssistant = async (v) => {
    try {
      await api.put("/claude/settings", { assistant_claude: v });
      toast.success(v ? "Assistant: Claude connector. Built-in email AI and in-app chat are off." : "Built-in assistant is back on.");
      load();
      setTimeout(() => window.location.reload(), 600);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const disconnect = async (c) => {
    if (!window.confirm(`Disconnect ${c.user_name}'s ${c.app}? Claude will have to sign in again.`)) return;
    try { await api.post(`/claude/connections/${c.connection_id}/disconnect`); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not disconnect"); }
  };
  return (
    <Card className="p-6 space-y-5" data-testid="claude-connector-card" id="claude-connector">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg grid place-items-center bg-[#D97757]/10 text-[#D97757] shrink-0"><Asterisk className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-black">Claude connector</h2>
          <p className="text-sm text-slate-500 mt-0.5">Connect Claude so it can look things up and build estimates in GridForge with your access. Every change shows as "you via Claude" and can be undone on the bid. Claude can prepare email drafts for you to review and send, but it can't send emails or proposals, approve sign-offs, record won or lost, or delete records.</p>
        </div>
        {s.can_switch ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-500">{s.enabled ? "On" : "Off"}</span>
            <Switch data-testid="claude-master-switch" checked={!!s.enabled} onCheckedChange={toggle} />
          </div>
        ) : (
          <span data-testid="claude-connector-status" className={`text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 ${s.enabled ? "text-emerald-700 bg-emerald-50" : "text-slate-500 bg-slate-100"}`}>{s.enabled ? "On" : "Off"}</span>
        )}
      </div>
      {!s.enabled && <div data-testid="claude-connector-off" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">The connector is off for the whole company. Claude can't connect or make changes until an owner or admin turns it back on.</div>}
      <div className="flex items-start gap-3 rounded-lg border border-slate-200 p-3" data-testid="assistant-switch-row">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800">Assistant: Claude connector</div>
          <p className="text-xs text-slate-500 mt-0.5">On: the built-in email AI and the in-app Sol chat are off. Drafting, replies and anything with judgment come through Claude (draft_email). Gmail sync that logs replies, 7 AM reminders and the lead intake queue keep running.</p>
        </div>
        {s.can_switch ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-500">{s.assistant_claude ? "On" : "Off"}</span>
            <Switch data-testid="assistant-claude-switch" checked={!!s.assistant_claude} onCheckedChange={toggleAssistant} />
          </div>
        ) : (
          <span data-testid="assistant-claude-status" className={`text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 ${s.assistant_claude ? "text-emerald-700 bg-emerald-50" : "text-slate-500 bg-slate-100"}`}>{s.assistant_claude ? "On" : "Off"}</span>
        )}
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Connector address</div>
        <CopyField value={CONNECTOR_URL} testid="claude-connector-url" />
      </div>
      <SetupSteps />
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{s.is_leader ? "Connections across the company" : "Your connections"}</div>
        <Connections s={s} onDisconnect={disconnect} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Claude's recent activity</div>
        <RecentActivity items={s.activity} />
      </div>
    </Card>
  );
}
